// frontend/src/presentation/hooks/useBluetoothButton.ts
//
// Hook sprzętowego przycisku Push-To-Talk słuchawki Bluetooth (Media Session API).
//
// Słuchawka sparowana z systemem operacyjnym wysyła zdarzenia transportu
// multimediów. Przechwytujemy je przez `navigator.mediaSession.setActionHandler`
// i mapujemy na start/stop istniejącego systemu dyktowania:
//
//   'play'          → onStartRecording   (rozpocznij nagrywanie)
//   'pause'         → onStopRecording    (zakończ nagrywanie)
//   'nexttrack'     → toggle nagrywania  (część słuchawek wysyła tylko ten sygnał
//   'previoustrack' → toggle nagrywania   na oba kliknięcia jednego przycisku)
//
// Stan nagrywania trzymamy lokalnie (`recordingRef`): dzięki temu 'play'/'pause'
// są idempotentne, a 'nexttrack'/'previoustrack' działają jak przełącznik.
//
// Handlery montujemy raz (useEffect z pustą listą zależności), a świeże referencje
// callbacków czytamy przez ref — re-render rodzica nie odpina integracji. Funkcja
// czyszcząca zeruje wszystkie handlery (ustawia je na `null`).

import { useEffect, useRef } from 'react'

/** Callbacki spinające przycisk sprzętowy z systemem dyktowania. */
export interface UseBluetoothButtonOptions {
  /** Wywoływane, gdy słuchawka żąda rozpoczęcia nagrywania ('play' / toggle). */
  onStartRecording: () => void
  /** Wywoływane, gdy słuchawka żąda zakończenia nagrywania ('pause' / toggle). */
  onStopRecording: () => void
}

/**
 * Podpina fizyczny przycisk słuchawki Bluetooth (Push-To-Talk) pod przekazane
 * callbacki start/stop nagrywania. Nic nie zwraca — cała integracja żyje w efekcie.
 *
 * Bezpieczny w środowiskach bez Media Session API (JSDOM, starsze przeglądarki):
 * wtedy hook nie robi nic.
 */
export function useBluetoothButton({
  onStartRecording,
  onStopRecording,
}: UseBluetoothButtonOptions): void {
  // Stabilne referencje — nowa tożsamość callbacków przy re-renderze rodzica
  // nie może odpinać handlerów Media Session (montujemy je tylko raz).
  const onStartRef = useRef(onStartRecording)
  const onStopRef = useRef(onStopRecording)
  onStartRef.current = onStartRecording
  onStopRef.current = onStopRecording

  // Lokalny stan nagrywania — źródło prawdy dla przełącznika
  // 'nexttrack'/'previoustrack' oraz strażnik idempotencji 'play'/'pause'.
  const recordingRef = useRef(false)

  useEffect(() => {
    const mediaSession =
      typeof navigator !== 'undefined' ? navigator.mediaSession : undefined
    if (!mediaSession) return

    const startRecording = (): void => {
      if (recordingRef.current) return
      recordingRef.current = true
      onStartRef.current()
    }

    const stopRecording = (): void => {
      if (!recordingRef.current) return
      recordingRef.current = false
      onStopRef.current()
    }

    const toggleRecording = (): void => {
      if (recordingRef.current) stopRecording()
      else startRecording()
    }

    const bindings: ReadonlyArray<
      readonly [MediaSessionAction, MediaSessionActionHandler]
    > = [
      ['play', startRecording],
      ['pause', stopRecording],
      ['nexttrack', toggleRecording],
      ['previoustrack', toggleRecording],
    ]

    for (const [action, handler] of bindings) {
      try {
        mediaSession.setActionHandler(action, handler)
      } catch {
        // Przeglądarka nie wspiera tej akcji (np. starsze Safari) — pomijamy,
        // pozostałe handlery działają dalej.
      }
    }

    return () => {
      for (const [action] of bindings) {
        try {
          mediaSession.setActionHandler(action, null)
        } catch {
          // jw. — cichy no-op przy sprzątaniu.
        }
      }
      recordingRef.current = false
    }
  }, [])
}
